import { useEffect, useMemo, useRef, useState } from 'react'
import { forwardMessages } from '../lib/data'
import { useT } from '../lib/i18n'
import { chatAvatarUrl, chatTitle, sortChats } from '../lib/chat'
import { useAuthStore } from '../stores/authStore'
import { useChatStore } from '../stores/chatStore'
import { Avatar } from './Avatar'
import { IconButton } from './IconButton'
import { Spinner } from './Spinner'
import { CloseIcon } from './Icons'

type ForwardPickerProps = {
  /** What is being forwarded: one message from a bubble's menu, or the thread's whole selection. */
  messageIds: string[]
  onClose: () => void
  /** The copies were written into `count` chats; the caller says so and leaves selection mode. */
  onSent: (count: number) => void
}

/**
 * "Forward to" (W4) — the caller's own chats, read from the store the chat list already renders, so
 * the sheet needs no request of its own. The shape is `AddMembersPicker`'s: bottom sheet on a
 * phone, centred panel on desktop, focus on open, Escape closes, Tab is kept inside, and the scrim
 * cancels — until the send is in flight, when the sheet stays up for its answer.
 *
 * Every chat is offered, the one the message came from included: the api only asks that both ends
 * be the caller's, forwarding a photo back where it came from is legal, and hiding the current chat
 * would be a second rule nobody set.
 */
export function ForwardPicker({ messageIds, onClose, onSent }: ForwardPickerProps) {
  const t = useT()
  const meId = useAuthStore((state) => state.user?.id ?? null)
  const chats = useChatStore((state) => state.chats)
  const [picked, setPicked] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const dialogRef = useRef<HTMLDivElement>(null)

  // The same order as the chat list, so the sheet is not a second mental map of the same chats.
  const targets = useMemo(() => sortChats(Object.values(chats)), [chats])

  useEffect(() => {
    const dialog = dialogRef.current
    dialog?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Not while the send is in flight: the sheet stays up until its answer lands, so a
        // dismissal cannot leave a request nobody is waiting for.
        if (!busy) onClose()
        return
      }
      if (event.key !== 'Tab' || !dialog) return
      const focusable = dialog.querySelectorAll<HTMLElement>(
        'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
      )
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (!first || !last) return
      const active = document.activeElement
      if (event.shiftKey && (active === first || active === dialog)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [busy, onClose])

  const toggle = (chatId: string) => {
    setPicked((previous) =>
      previous.includes(chatId) ? previous.filter((id) => id !== chatId) : [...previous, chatId],
    )
  }

  const send = async () => {
    if (picked.length === 0 || busy) return
    setBusy(true)
    setError(null)
    try {
      await forwardMessages(messageIds, picked)
      onSent(picked.length)
    } catch {
      // The sheet stays open with the picks intact: a failure is one press away from a retry.
      setError(t('chat.forwardFailed'))
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-app/80 backdrop-blur-sm sm:items-center sm:p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose()
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="forward-heading"
        tabIndex={-1}
        className="sheet-enter flex max-h-[85dvh] w-full max-w-md flex-col rounded-t-panel bg-surface pb-[var(--sab)] outline-none shadow-lift ring-1 ring-line-strong sm:rounded-panel sm:pb-0"
      >
        <header className="flex items-center gap-2 border-b border-line px-3 py-2.5">
          <h2 id="forward-heading" className="min-w-0 flex-1 truncate text-title font-semibold text-ink">
            {t('chat.forwardTitle')}
          </h2>
          <IconButton label={t('chat.close')} size={36} disabled={busy} onClick={onClose}>
            <CloseIcon size={18} />
          </IconButton>
        </header>

        <p role="status" className="px-4 pt-3 text-ui text-muted">
          {picked.length === 0 ? t('chat.forwardPick') : t('chat.selected', { count: picked.length })}
        </p>

        {error && (
          <p role="alert" className="px-4 pt-2 text-meta text-danger">
            {error}
          </p>
        )}

        <div className="chat-scroll mt-2 min-h-0 flex-1">
          {targets.length === 0 ? (
            <p className="p-6 text-center text-ui text-muted">{t('chat.noChats')}</p>
          ) : (
            <ul className="flex flex-col p-2">
              {targets.map((chat) => {
                const pickedChat = picked.includes(chat.id)
                const title = chatTitle(chat, meId)
                return (
                  <li key={chat.id}>
                    <label
                      className={`flex min-h-14 cursor-pointer items-center gap-3 rounded-panel px-2 py-2 transition-colors duration-quick ${
                        pickedChat ? 'bg-surface2' : 'hover:bg-surface2/60'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="size-5 shrink-0 accent-accent"
                        checked={pickedChat}
                        disabled={busy}
                        onChange={() => toggle(chat.id)}
                      />
                      <Avatar name={title} path={chatAvatarUrl(chat, meId)} size={40} />
                      <span className="min-w-0 flex-1 truncate text-ui text-ink">
                        <bdi>{title}</bdi>
                      </span>
                    </label>
                  </li>
                )
              })}
            </ul>
          )}
        </div>

        <div className="flex items-center gap-2 border-t border-line p-3">
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="min-h-11 flex-1 rounded-full text-ui font-medium text-ink ring-1 ring-line ring-inset transition-colors duration-quick hover:bg-surface2 disabled:opacity-50"
          >
            {t('chat.cancel')}
          </button>
          <button
            type="button"
            disabled={picked.length === 0 || busy}
            onClick={() => void send()}
            className="press flex min-h-11 flex-1 items-center justify-center gap-2 rounded-full bg-accent px-4 text-ui font-semibold text-on-accent hover:bg-accent-strong disabled:opacity-40"
          >
            {busy && <Spinner size={16} label={t('chat.forwarding')} />}
            {t(busy ? 'chat.forwarding' : 'chat.forwardSend')}
          </button>
        </div>
      </div>
    </div>
  )
}
